import { lazy, Suspense, useEffect, useRef } from 'react'
import { Routes, Route, Navigate, useSearchParams, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@common/auth'
import { useAbility } from '@common/abilities'
import { LoadingIndicator } from '@common/components/LoadingIndicator'

/**
 * Application routes — mirrors the AngularJS ui-router state tree EXACTLY.
 *
 * ALL 16 MODULES MIGRATED
 * Routes are lazy-loaded for optimal code-splitting.
 * Every path segment matches the legacy AngularJS URL so that
 * HashRouter produces identical /#/... URLs.
 */

// ========== Lazy-loaded module imports ==========

// Priority 1-3: Core modules (loaded eagerly for fast first paint)
import {
    DashboardLayout,
    DisputesDashboardPage,
    DisputeTeamPerformancePage,
} from '@/app/dashboard'

// Priority 1-2: Analytics + Reporting (lazy)
const AnalyticsModule = lazy(() =>
    import('@/app/analytics').then((m) => ({
        default: () => <m.AnalyticsLayout />,
    }))
)
const AnalyticsReportPage = lazy(() =>
    import('@/app/analytics').then((m) => ({ default: m.AnalyticsReportPage }))
)
const ReportingLayout = lazy(() =>
    import('@/app/reporting').then((m) => ({ default: m.ReportingLayout }))
)
const CipAnalysisPage = lazy(() =>
    import('@/app/reporting').then((m) => ({ default: m.CipAnalysisPage }))
)
const CipDefectReportPage = lazy(() =>
    import('@/app/reporting').then((m) => ({ default: m.CipDefectReportPage }))
)
const CustomRiskReportPage = lazy(() =>
    import('@/app/reporting').then((m) => ({ default: m.CustomRiskReportPage }))
)
const ArmReportPage = lazy(() =>
    import('@/app/reporting').then((m) => ({ default: m.ArmReportPage }))
)

// Priority 4-6: Small modules (lazy)
const BrandingLayout = lazy(() =>
    import('@/app/branding').then((m) => ({ default: m.BrandingLayout }))
)
const BrandingConfigPage = lazy(() =>
    import('@/app/branding').then((m) => ({ default: m.BrandingConfigPage }))
)
const IssuingBankLayout = lazy(() =>
    import('@/app/issuingBank').then((m) => ({ default: m.IssuingBankLayout }))
)
const IntegrationSettingsPage = lazy(() =>
    import('@/app/issuingBank').then((m) => ({ default: m.IntegrationSettingsPage }))
)
const ExclusionSettingsPage = lazy(() =>
    import('@/app/issuingBank').then((m) => ({ default: m.ExclusionSettingsPage }))
)
const PortalLayout = lazy(() =>
    import('@/app/portal').then((m) => ({ default: m.PortalLayout }))
)
const PortalCategoryPage = lazy(() =>
    import('@/app/portal').then((m) => ({ default: m.PortalCategoryPage }))
)
const PortalAPICategoryPage = lazy(() =>
    import('@/app/portal').then((m) => ({ default: m.PortalAPICategoryPage }))
)
const PortalLandingPage = lazy(() =>
    import('@/app/portal').then((m) => ({ default: m.PortalLandingPage }))
)

// Priority 7-11: Medium modules (lazy)

// Sanction List — lookup/results/batchArchive/cleared-customers are root-level routes
// sanctioned-countries is under /settings/
const SanctionListLayout = lazy(() =>
    import('@/app/sanctionList').then((m) => ({ default: m.SanctionListLayout }))
)
const LookupPage = lazy(() =>
    import('@/app/sanctionList').then((m) => ({ default: m.LookupPage }))
)
const ResultsPage = lazy(() =>
    import('@/app/sanctionList').then((m) => ({ default: m.ResultsPage }))
)
const BatchArchivePage = lazy(() =>
    import('@/app/sanctionList').then((m) => ({ default: m.BatchArchivePage }))
)
const ClearedCustomersPage = lazy(() =>
    import('@/app/sanctionList').then((m) => ({ default: m.ClearedCustomersPage }))
)
const SanctionedCountriesPage = lazy(() =>
    import('@/app/sanctionList').then((m) => ({ default: m.SanctionedCountriesPage }))
)
const DisputeTrendsLayout = lazy(() =>
    import('@/app/disputeTrends').then((m) => ({ default: m.DisputeTrendsLayout }))
)
const DisputeTrendsTransactionsPage = lazy(() =>
    import('@/app/disputeTrends').then((m) => ({ default: m.DisputeTrendsTransactionsPage }))
)
const WatchGroupLayout = lazy(() =>
    import('@/app/disputeTrends').then((m) => ({ default: m.WatchGroupLayout }))
)
const WatchGroupTransactionsPage = lazy(() =>
    import('@/app/disputeTrends').then((m) => ({ default: m.WatchGroupTransactionsPage }))
)
const WatchGroupRemovedPage = lazy(() =>
    import('@/app/disputeTrends').then((m) => ({ default: m.WatchGroupRemovedPage }))
)
const WatchGroupChangeLogPage = lazy(() =>
    import('@/app/disputeTrends').then((m) => ({ default: m.WatchGroupChangeLogPage }))
)
const WatchlistCasesPage = lazy(() =>
    import('@/app/watchlistCases').then((m) => ({ default: m.WatchlistCasesPage }))
)
const WatchlistCaseLayout = lazy(() =>
    import('@/app/watchlistCases').then((m) => ({ default: m.WatchlistCaseLayout }))
)
const CaseHistoryPage = lazy(() =>
    import('@/app/watchlistCases').then((m) => ({ default: m.CaseHistoryPage }))
)
const CaseSourceDataPage = lazy(() =>
    import('@/app/watchlistCases').then((m) => ({ default: m.CaseSourceDataPage }))
)
const CaseNotesPage = lazy(() =>
    import('@/app/watchlistCases').then((m) => ({ default: m.CaseNotesPage }))
)
const CaseMatchReviewPage = lazy(() =>
    import('@/app/watchlistCases').then((m) => ({ default: m.CaseMatchReviewPage }))
)
const CaseFilesPage = lazy(() =>
    import('@/app/watchlistCases').then((m) => ({ default: m.CaseFilesPage }))
)
const DocManagementLayout = lazy(() =>
    import('@/app/docManagement').then((m) => ({ default: m.DocManagementLayout }))
)
const QueuesPage = lazy(() =>
    import('@/app/docManagement').then((m) => ({ default: m.QueuesPage }))
)
const QueueDocumentsPage = lazy(() =>
    import('@/app/docManagement').then((m) => ({ default: m.QueueDocumentsPage }))
)
const UploadDocumentsPage = lazy(() =>
    import('@/app/docManagement').then((m) => ({ default: m.UploadDocumentsPage }))
)
const SharedDocumentsPage = lazy(() =>
    import('@/app/docManagement').then((m) => ({ default: m.SharedDocumentsPage }))
)
const SharedDocumentsForClientPage = lazy(() =>
    import('@/app/docManagement').then((m) => ({ default: m.SharedDocumentsForClientPage }))
)
const ManageQueuesPage = lazy(() =>
    import('@/app/docManagement').then((m) => ({ default: m.ManageQueuesPage }))
)
const ArchiveDocumentsPage = lazy(() =>
    import('@/app/docManagement').then((m) => ({ default: m.ArchiveDocumentsPage }))
)
const ReviewsLayout = lazy(() =>
    import('@/app/reviews').then((m) => ({ default: m.ReviewsLayout }))
)
const QuestionAndDenialReviewsPage = lazy(() =>
    import('@/app/reviews').then((m) => ({ default: m.QuestionAndDenialReviewsPage }))
)
const ChargeOffReviewsPage = lazy(() =>
    import('@/app/reviews').then((m) => ({ default: m.ChargeOffReviewsPage }))
)
const LexisVerificationLayout = lazy(() =>
    import('@/app/reviews').then((m) => ({ default: m.LexisVerificationLayout }))
)
// BestAddressPage and FlexIdPage are rendered inline by LexisVerificationLayout
// (matching Angular uib-tabset dual-panel DOM), no longer used as route elements.

// Priority 12-13: Complex modules (lazy)
const CompCardsLayout = lazy(() =>
    import('@/app/compCards').then((m) => ({ default: m.CompCardsLayout }))
)
const CompCardCriteriaPage = lazy(() =>
    import('@/app/compCards').then((m) => ({ default: m.CompCardCriteriaPage }))
)
const CompCardAlertsLayout = lazy(() =>
    import('@/app/compCards').then((m) => ({ default: m.CompCardAlertsLayout }))
)
const CompCardAlertsListPage = lazy(() =>
    import('@/app/compCards').then((m) => ({ default: m.CompCardAlertsListPage }))
)
const CompCardAlertGroupsPage = lazy(() =>
    import('@/app/compCards').then((m) => ({ default: m.CompCardAlertGroupsPage }))
)
const CompCardReissuesPage = lazy(() =>
    import('@/app/compCards').then((m) => ({ default: m.CompCardReissuesPage }))
)
const CompCardReissueLayout = lazy(() =>
    import('@/app/compCards').then((m) => ({ default: m.CompCardReissueLayout }))
)
const CompCardSummaryPage = lazy(() =>
    import('@/app/compCards').then((m) => ({ default: m.CompCardSummaryPage }))
)
const CompCardHistoryPage = lazy(() =>
    import('@/app/compCards').then((m) => ({ default: m.CompCardHistoryPage }))
)
const CompCardRelatedCardsLayout = lazy(() =>
    import('@/app/compCards').then((m) => ({ default: m.CompCardRelatedCardsLayout }))
)
const CardsForReissuePage = lazy(() =>
    import('@/app/compCards').then((m) => ({ default: m.CardsForReissuePage }))
)
const DeniedCardsPage = lazy(() =>
    import('@/app/compCards').then((m) => ({ default: m.DeniedCardsPage }))
)
const RecommendationsPage = lazy(() =>
    import('@/app/compCards').then((m) => ({ default: m.RecommendationsPage }))
)
const RelatedProgramsPage = lazy(() =>
    import('@/app/compCards').then((m) => ({ default: m.RelatedProgramsPage }))
)
const CallBlastPage = lazy(() =>
    import('@/app/compCards').then((m) => ({ default: m.CallBlastPage }))
)
const CompCardFilesPage = lazy(() =>
    import('@/app/compCards').then((m) => ({ default: m.CompCardFilesPage }))
)
const CsrInquiriesLayout = lazy(() =>
    import('@/app/csrInquiries').then((m) => ({ default: m.CsrInquiriesLayout }))
)
const CsrSearchPage = lazy(() =>
    import('@/app/csrInquiries').then((m) => ({ default: m.CsrSearchPage }))
)

const CsrNewDisputeLayout = lazy(() =>
    import('@/app/csrInquiries').then((m) => ({ default: m.CsrNewDisputeLayout }))
)
const CsrTransactionsPage = lazy(() =>
    import('@/app/csrInquiries').then((m) => ({ default: m.CsrTransactionsPage }))
)
const CsrCommentsPage = lazy(() =>
    import('@/app/csrInquiries').then((m) => ({ default: m.CsrCommentsPage }))
)
const CsrQuestionsPage = lazy(() =>
    import('@/app/csrInquiries').then((m) => ({ default: m.CsrQuestionsPage }))
)
const CsrDisclosuresPage = lazy(() =>
    import('@/app/csrInquiries').then((m) => ({ default: m.CsrDisclosuresPage }))
)
const CsrConfirmationPage = lazy(() =>
    import('@/app/csrInquiries').then((m) => ({ default: m.CsrConfirmationPage }))
)

// Priority 14-16: Largest modules (lazy)
const ArmLayout = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmLayout }))
)
const ArmCasesPage = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmCasesPage }))
)
const ArmCaseDetailLayout = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmAlertContainer }))
)
const ArmCaseSummaryPage = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmCaseSummaryPage }))
)
const ArmCaseHistoryPage = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmCaseHistoryPage }))
)
const ArmCaseTransactionsPage = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmCaseTransactionsPage }))
)
const ArmCaseNotesPage = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmCaseNotesPage }))
)
const ArmCaseFilesPage = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmCaseFilesPage }))
)
const ArmSettingsPage = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmSettingsPage }))
)
// ARM additional page placeholders
const ArmAlertQueuePage = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmAlertQueuePage }))
)
const ArmCaseQueuePage = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmCaseQueuePage }))
)
const ArmCaseCardDetailsPage = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmCaseCardDetailsPage }))
)
const ArmCaseCustomerDataPage = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmCaseCustomerDataPage }))
)
const ArmCaseReportsPage = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmCaseReportsPage }))
)
const ArmCaseLinkedAlertsPage = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmCaseLinkedAlertsPage }))
)
const ArmCaseSarFormsLayout = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmCaseSarFormsLayout }))
)
const ArmSarFormsListPage = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmSarFormsListPage }))
)
const ArmSarFormLayout = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmSarFormLayout }))
)
const ArmSarFormHomePage = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmSarFormHomePage }))
)
const ArmSarFormContactInfoPage = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmSarFormContactInfoPage }))
)
const ArmSarFormActivityOccurredPage = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmSarFormActivityOccurredPage }))
)
const ArmSarFormSubjectInfoPage = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmSarFormSubjectInfoPage }))
)
const ArmSarFormSuspiciousActivityPage = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmSarFormSuspiciousActivityPage }))
)
const ArmSarFormNarrativePage = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmSarFormNarrativePage }))
)
const ArmBsaClosurePage = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmBsaClosurePage }))
)
const ArmIrfReviewPage = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmIrfReviewPage }))
)
const ArmBsaReviewPage = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmBsaReviewPage }))
)
const ArmSarReviewsPage = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmSarReviewsPage }))
)
const ArmSarReviewListPage = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmSarReviewListPage }))
)
const ArmSarFormReviewContainerLayout = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmSarFormReviewContainerLayout }))
)
const ArmSarFiledPage = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmSarFiledPage }))
)
const SarFormBatchesPage = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.SarFormBatchesPage }))
)
const ArmManageQueuesPage = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmManageQueuesPage }))
)
const ArmSuspiciousActivityTypesPage = lazy(() =>
    import('@/app/arm').then((m) => ({ default: m.ArmSuspiciousActivityTypesPage }))
)

// Disputes additional page placeholders
const DisputesLayout = lazy(() =>
    import('@/app/disputes').then((m) => ({ default: m.DisputesLayout }))
)
const DisputeSearchPage = lazy(() =>
    import('@/app/disputes').then((m) => ({ default: m.DisputeSearchPage }))
)
const CaseActivityDashboardPage = lazy(() =>
    import('@/app/disputes').then((m) => ({ default: m.CaseActivityDashboardPage }))
)
const DisputeResultsPage = lazy(() =>
    import('@/app/disputes').then((m) => ({ default: m.DisputeResultsPage }))
)
const DisputeDetailLayout = lazy(() =>
    import('@/app/disputes').then((m) => ({ default: m.DisputeDetailLayout }))
)
const DisputeSummaryPage = lazy(() =>
    import('@/app/disputes').then((m) => ({ default: m.DisputeSummaryPage }))
)
const DisputeHistoryPage = lazy(() =>
    import('@/app/disputes').then((m) => ({ default: m.DisputeHistoryPage }))
)
const DisputeTransactionsPage = lazy(() =>
    import('@/app/disputes').then((m) => ({ default: m.DisputeTransactionsPage }))
)
const DisputeNotesPage = lazy(() =>
    import('@/app/disputes').then((m) => ({ default: m.DisputeNotesPage }))
)
const DisputeFilesPage = lazy(() =>
    import('@/app/disputes').then((m) => ({ default: m.DisputeFilesPage }))
)
const DisputeLettersPage = lazy(() =>
    import('@/app/disputes').then((m) => ({ default: m.DisputeLettersPage }))
)
const DisputeCommunicationPage = lazy(() =>
    import('@/app/disputes').then((m) => ({ default: m.DisputeCommunicationPage }))
)
const DisputeCardDetailsPage = lazy(() =>
    import('@/app/disputes').then((m) => ({ default: m.DisputeCardDetailsPage }))
)
const DisputeCardholderPage = lazy(() =>
    import('@/app/disputes').then((m) => ({ default: m.DisputeCardholderPage }))
)
const DisputeCallCenterNotesPage = lazy(() =>
    import('@/app/disputes').then((m) => ({ default: m.DisputeCallCenterNotesPage }))
)
const DisputeTransactionHistoryPage = lazy(() =>
    import('@/app/disputes').then((m) => ({ default: m.DisputeTransactionHistoryPage }))
)
const DisputeFeesPage = lazy(() =>
    import('@/app/disputes').then((m) => ({ default: m.DisputeFeesPage }))
)
const TransactionsPage = lazy(() =>
    import('@/app/disputes').then((m) => ({ default: m.TransactionsPage }))
)
const RegEDisputesPage = lazy(() =>
    import('@/app/disputes').then((m) => ({ default: m.RegEDisputesPage }))
)
const RegZDisputesPage = lazy(() =>
    import('@/app/disputes').then((m) => ({ default: m.RegZDisputesPage }))
)
const ProcessorApiLogPage = lazy(() =>
    import('@/app/disputes').then((m) => ({ default: m.ProcessorApiLogPage }))
)
const MastercomRequestLogPage = lazy(() =>
    import('@/app/disputes').then((m) => ({ default: m.MastercomRequestLogPage }))
)
const ManualCaseEntryPage = lazy(() =>
    import('@/app/disputes').then((m) => ({ default: m.ManualCaseEntryPage }))
)
const CasesProvisionalCreditPage = lazy(() =>
    import('@/app/disputes').then((m) => ({ default: m.CasesProvisionalCreditPage }))
)

// Manual Case Entry wizard (Step-based wizard with layout + child routes)
const ManualCaseEntryLayout = lazy(() =>
    import('@/app/disputes/manualCaseEntry').then((m) => ({ default: m.ManualCaseEntryLayout }))
)
const ManualCaseEntryNewCaseTypePage = lazy(() =>
    import('@/app/disputes/manualCaseEntry').then((m) => ({ default: m.ManualCaseEntryNewCaseTypePage }))
)
const ManualCaseEntryCardPage = lazy(() =>
    import('@/app/disputes/manualCaseEntry').then((m) => ({ default: m.ManualCaseEntryCardPage }))
)
const ManualCaseEntryRelatedTransactionsPage = lazy(() =>
    import('@/app/disputes/manualCaseEntry').then((m) => ({ default: m.ManualCaseEntryRelatedTransactionsPage }))
)
const ManualCaseEntryCaseInfoPage = lazy(() =>
    import('@/app/disputes/manualCaseEntry').then((m) => ({ default: m.ManualCaseEntryCaseInfoPage }))
)

// Admin Settings (lazy)
const AdminSettingsLayout = lazy(() =>
    import('@/app/settings').then((m) => ({ default: m.AdminSettingsLayout }))
)
const AdminSettingsPage = lazy(() =>
    import('@/app/settings').then((m) => ({ default: m.AdminSettingsPage }))
)
const NoPermissionsPage = lazy(() =>
    import('@/app/settings').then((m) => ({ default: m.NoPermissionsPage }))
)
const AdminAuthenticationSettingsPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminAuthenticationSettingsPage }))
)

// Admin sub-module pages (lazy) — Phase 1 placeholder components
const AdminIssuingBanksPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminIssuingBanksPage }))
)
const AdminProcessorsPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminProcessorsPage }))
)
const AdminReportPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminReportPage }))
)
const AdminBinsPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminBinsPage }))
)
const AdminFiidsPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminFiidsPage }))
)
const AdminCipProgramsPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminCipProgramsPage }))
)
const AdminDisputeProgramsPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminDisputeProgramsPage }))
)
const AdminActivateProgramsPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminActivateProgramsPage }))
)
const AdminIssuingBankReportArchivePage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminIssuingBankReportArchivePage }))
)
const AdminCustomersPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminCustomersPage }))
)
const CustomerDetailLayout = lazy(() =>
    import('@/app/admin/customers').then((m) => ({ default: m.CustomerDetailLayout }))
)
const CustomerDetailsPage = lazy(() =>
    import('@/app/admin/customers').then((m) => ({ default: m.CustomerDetailsPage }))
)
const CustomerContactBillingPage = lazy(() =>
    import('@/app/admin/customers').then((m) => ({ default: m.CustomerContactBillingPage }))
)
const CustomerUsersPage = lazy(() =>
    import('@/app/admin/customers').then((m) => ({ default: m.CustomerUsersPage }))
)
const CustomerIssuingBanksPage = lazy(() =>
    import('@/app/admin/customers').then((m) => ({ default: m.CustomerIssuingBanksPage }))
)
const CustomerProcessorsPage = lazy(() =>
    import('@/app/admin/customers').then((m) => ({ default: m.CustomerProcessorsPage }))
)
const CustomerStandardClientPage = lazy(() =>
    import('@/app/admin/customers').then((m) => ({ default: m.CustomerStandardClientPage }))
)
const CustomerExternalTeamsPage = lazy(() =>
    import('@/app/admin/customers').then((m) => ({ default: m.CustomerExternalTeamsPage }))
)
const CustomerDataSourcePage = lazy(() =>
    import('@/app/admin/customers').then((m) => ({ default: m.CustomerDataSourcePage }))
)
const CustomerDataProviderPage = lazy(() =>
    import('@/app/admin/customers').then((m) => ({ default: m.CustomerDataProviderPage }))
)
const CustomerBestAddressPage = lazy(() =>
    import('@/app/admin/customers').then((m) => ({ default: m.CustomerBestAddressPage }))
)
const CustomerDisputeAdvantagePage = lazy(() =>
    import('@/app/admin/customers').then((m) => ({ default: m.CustomerDisputeAdvantagePage }))
)
const CustomerCsrScriptPage = lazy(() =>
    import('@/app/admin/customers').then((m) => ({ default: m.CustomerCsrScriptPage }))
)
const CustomerWatchlistsPage = lazy(() =>
    import('@/app/admin/customers').then((m) => ({ default: m.CustomerWatchlistsPage }))
)
const AdminIntegrationUsersPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminIntegrationUsersPage }))
)
const AdminDisputeCaseDataApiPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminDisputeCaseDataApiPage }))
)
const AdminClientIntegrationSettingsPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminClientIntegrationSettingsPage }))
)
const AdminCipIntegrationSettingsPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminCipIntegrationSettingsPage }))
)
const AdminChangeOfAddressSettingsPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminChangeOfAddressSettingsPage }))
)
const AdminTcIntegrationSettingsPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminTcIntegrationSettingsPage }))
)
const AdminAddressVerificationSettingsPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminAddressVerificationSettingsPage }))
)
const AdminCipPmReportSettingsPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminCipPmReportSettingsPage }))
)
const AdminPortalIntegrationSettingsPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminPortalIntegrationSettingsPage }))
)
const AdminExclusionListPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminExclusionListPage }))
)
const AdminIssuingBankExclusionListPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminIssuingBankExclusionListPage }))
)
const AdminDisputeIntegrationSettingsPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminDisputeIntegrationSettingsPage }))
)
const AdminProcessorApiIntegrationSettingsPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminProcessorApiIntegrationSettingsPage }))
)
const AdminModelTestIntegrationSettingsPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminModelTestIntegrationSettingsPage }))
)
const AdminClientUsersPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminClientUsersPage }))
)
const AdminManageUserLayout = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminManageUserLayout }))
)
const AdminDisputeProfilesPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminDisputeProfilesPage }))
)
const AdminManageDisputeProfileLayout = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminManageDisputeProfileLayout }))
)
const AdminBusinessLinesPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminBusinessLinesPage }))
)
const AdminDisputeCaseSettingsPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminDisputeCaseSettingsPage }))
)
const AdminDisputeCaseTimeframeSettingsPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminDisputeCaseTimeframeSettingsPage }))
)
const AdminDisputeReasonPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminDisputeReasonPage }))
)
const AdminRiskCodeManagementPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminRiskCodeManagementPage }))
)
const AdminWorkflowsPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminWorkflowsPage }))
)
const WorkflowDesigner = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.WorkflowDesigner }))
)
const AdminCaseLettersLayout = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminCaseLettersLayout }))
)
const AdminPreDefinedFieldsPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminPreDefinedFieldsPage }))
)
const AdminPrintExportFieldsPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminPrintExportFieldsPage }))
)
const AdminDisputeLetterPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminDisputeLetterPage }))
)
const AdminAdvancedSettingsPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminAdvancedSettingsPage }))
)
const AdminProgramTypesPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminProgramTypesPage }))
)
const AdminDisputeCloseReasonPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminDisputeCloseReasonPage }))
)
const AdminTransCodesPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminTransCodesPage }))
)
const AdminCallCenterAssignmentsLayout = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminCallCenterAssignmentsLayout }))
)
const AdminMyCallCentersPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminMyCallCentersPage }))
)
const AdminAssignmentsByBinPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminAssignmentsByBinPage }))
)
const AdminRedFlagReportSettingsPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminRedFlagReportSettingsPage }))
)
const AdminArmDailyReportSettingsPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminArmDailyReportSettingsPage }))
)
const AdminBinAssignmentsPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminBinAssignmentsPage }))
)
const AdminAutoAssignPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminAutoAssignPage }))
)
const AdminWriteoffReasonPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminWriteoffReasonPage }))
)
const AdminIntelligentIntakePage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminIntelligentIntakePage }))
)
const AdminNoteTemplatesPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminNoteTemplatesPage }))
)
const AdminDisputePendsPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminDisputePendsPage }))
)
const AdminOperatingAccountsPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminOperatingAccountsPage }))
)
const AdminHolidayCalendarPage = lazy(() =>
    import('@/app/admin').then((m) => ({ default: m.AdminHolidayCalendarPage }))
)

// ========== Login page — matches legacy loginController.js ==========
// Shows the branded logo and IMMEDIATELY triggers signinRedirect(),
// exactly like the AngularJS loginController which calls $scope.signIn() on init.
function LoginPage() {
    const { login } = useAuth()
    const [searchParams] = useSearchParams()
    const location = useLocation()
    const redirected = useRef(false)

    useEffect(() => {
        if (redirected.current) return
        redirected.current = true

        const action = searchParams.get('action')
        const arg = searchParams.get('arg')
        const extraQueryParams: Record<string, string> = {}
        if (action && arg) {
            extraQueryParams.action = action
            extraQueryParams.arg = arg
        }

        // Preserve original URL through OIDC flow (matches Angular loginController.js
        // which passes .originalState as data.originalState to signinRedirect)
        const returnTo = (location.state as { returnTo?: string } | null)?.returnTo
        const stateData = returnTo && !returnTo.startsWith('/login') && !returnTo.startsWith('/logout')
            ? { originalPath: returnTo }
            : undefined

        login(extraQueryParams, stateData).catch(console.error)
    }, [login, searchParams, location.state])

    return (
        <div id="login-container">
            <main id="login-content">
                <div className="login-hd">
                    <div id="login-logo">
                        <img src="/assets/img/logo_aps_login_x.png" alt="" />
                    </div>
                </div>
            </main>
        </div>
    )
}

// ========== Login callback — matches legacy loginCallbackController.js ==========
// Handles the OIDC redirect callback: processes the auth response,
// then navigates to the original URL (if preserved) or /home.
// Mirrors Angular loginCallbackController.js which reads signinResponse.state.originalState
// and calls .go(originalState.name, originalState.params).
function LoginCallbackPage() {
    const { handleCallback } = useAuth()
    const navigate = useNavigate()
    const processed = useRef(false)

    useEffect(() => {
        if (processed.current) return
        processed.current = true

        handleCallback()
            .then((stateData) => {
                // Restore original URL if preserved through OIDC flow
                const originalPath = stateData?.originalPath as string | undefined
                if (originalPath && !originalPath.startsWith('/login') && !originalPath.startsWith('/logout')) {
                    navigate(originalPath, { replace: true })
                } else {
                    navigate('/home', { replace: true })
                }
            })
            .catch((err) => {
                console.error('Login callback error:', err)
                navigate('/login', { replace: true })
            })
    }, [handleCallback, navigate])

    return (
        <div id="app-body">
            <header id="global-header" className="dispute-fraud">
                <div className="header-col header-col-a">
                    <div id="aps-logo-hd"></div>
                </div>
                <div className="header-col header-col-b"></div>
            </header>
        </div>
    )
}

// ========== Silent callback — matches legacy loginSilentCallbackController.js ==========
function LoginSilentCallbackPage() {
    const { handleSilentCallback } = useAuth()
    const processed = useRef(false)

    useEffect(() => {
        if (processed.current) return
        processed.current = true
        handleSilentCallback().catch(console.error)
    }, [handleSilentCallback])

    return null
}

// ========== Logout callback — matches legacy logoutCallbackController.js ==========
// Processes the signout redirect response (logoutState.complete()), then navigates
// to the login page. The AngularJS controller calls signoutRedirectCallback() and
// then navigates to '/' (which shows the login page).
function LogoutCallbackPage() {
    const { handleLogoutCallback } = useAuth()
    const navigate = useNavigate()
    const processed = useRef(false)

    useEffect(() => {
        if (processed.current) return
        processed.current = true

        handleLogoutCallback()
            .then(() => {
                navigate('/login', { replace: true })
            })
            .catch((err) => {
                console.error('Logout callback error:', err)
                navigate('/login', { replace: true })
            })
    }, [handleLogoutCallback, navigate])

    return (
        <div id="app-body">
            <header id="global-header" className="dispute-fraud">
                <div className="header-col header-col-a">
                    <div id="aps-logo-hd"></div>
                </div>
                <div className="header-col header-col-b"></div>
            </header>
        </div>
    )
}

// ========== Home router — matches legacy home.js homeController ==========
// Checks user abilities in priority order and navigates to first authorized page.
// Exactly mirrors the AngularJS homeStateResolver behavior (home.js lines 65-134).
function HomeRouter() {
    const { hasAbility, userProfile, isProfileLoaded } = useAbility()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const resolved = useRef(false)

    useEffect(() => {
        if (!isProfileLoaded || resolved.current) return
        resolved.current = true

        const product = searchParams.get('product')

        // Priority-ordered ability→route map — matches home.js lines 79-131 EXACTLY.
        // The first matching ability wins.
        type RouteConfig = {
            ability?: string
            predicate?: () => boolean
            path: string
        }
        const routes: RouteConfig[] = []

        // Non-docManagement routes (skipped when product === 'docManagement')
        // Matches home.js lines 79-112
        if (product !== 'docManagement') {
            routes.push(
                { ability: 'ViewMyCases', path: '/disputes/list/debit/mycases' },
                { ability: 'ViewDashboard', path: '/dashboard/disputes' },
                { ability: 'ViewCustomers', path: '/settings/customers' },
                { ability: 'OfacLookup', path: '/batchArchive' },
                { ability: 'ManageProgramModels', path: '/settings/cip-programs' },
                { ability: 'ViewCIPIntegrationSettings', path: '/settings/cip-integration-settings' },
                { ability: 'ViewIntegrationClientSettings', path: '/settings/client-integration-settings' },
                { ability: 'GenerateCIPAnalysisReport', path: '/reporting/cip-analysis' },
                { ability: 'ViewExclusionList', path: '/settings/exclusion-list' },
                { ability: 'CreateBankIntegrationSettings', path: '/settings/issuingBank/settings' },
                { ability: 'ViewDisputes', path: '/disputes/list/debit' },
                // ViewDisputeTeamPerformance requires disputeCaseDataSource === 'API'
                // (matches home.js line 91 — createApiClientPredicate)
                {
                    predicate: () =>
                        hasAbility('ViewDisputeTeamPerformance') &&
                        userProfile?.disputeCaseDataSource === 'API',
                    path: '/dashboard/dispute-team-performance',
                },
                { ability: 'ViewHolidayCalendar', path: '/settings/holiday-calendar' },
                { ability: 'ViewDisputeIntegrationSettings', path: '/settings/dispute-integration-settings' },
                { ability: 'ViewModelTestIntegrationSettings', path: '/settings/model-test-integration-settings' },
                { ability: 'ViewFraudCase', path: '/arm-cases' },
                { ability: 'ViewFraudAlertQueues', path: '/arm-alerts' },
                { ability: 'ViewCompCardAlerts', path: '/comp-cards/alerts' },
                { ability: 'ViewDisputeTrendTransactions', path: '/dispute-trends/transactions' },
                { ability: 'ViewVerificationRequests', path: '/reviews/verification/flexId' },
                { ability: 'ViewClearedCustomers', path: '/cleared-customers' },
                { ability: 'ViewCustomRiskReport', path: '/reporting/custom-risk-report' },
                { ability: 'ManageWorkflows', path: '/settings/workflows' },
                { ability: 'Watchlist-ManageCase', path: '/watchlistCases' },
                { ability: 'ManageBINs', path: '/settings/bins' },
                { ability: 'ManageFIIDs', path: '/settings/fiids' },
                { ability: 'ManageBrand', path: '/settings/branding/config' },
                { ability: 'Watchlist-ManageSanctionedCountries', path: '/settings/sanctioned-countries' },
                { ability: 'ViewDisputeCaseDataApiSettings', path: '/settings/dispute-case-data-api' },
                { ability: 'CSRCreateCase', path: '/inquiries/new' },
                { ability: 'ViewFraudDailyReportIntegrationSettings', path: '/settings/arm-daily-report-integration-settings' },
                { ability: 'ManageFraudAlertQueues', path: '/settings/arm/manage-queues' },
            )
        }

        // Doc Management routes — kept last before clientUsers/authSettings
        // (matches home.js lines 124-125)
        routes.push(
            {
                predicate: () =>
                    hasAbility('ViewDocumentManagementQueues') &&
                    (!product || product === 'docManagement'),
                path: '/docmgmt/queues',
            },
            {
                predicate: () =>
                    hasAbility('ViewDocumentManagementSharedDocuments') &&
                    (!product || product === 'docManagement'),
                path: '/docmgmt/shared-documents',
            },
        )

        // Last resort routes (matches home.js lines 127-128)
        routes.push(
            { ability: 'ViewClientUsers', path: '/settings/client-users' },
            { ability: 'UpdateAuthenticationSettings', path: '/authentication-settings' },
        )

        // Find first authorized route (matches home.js lines 44-58)
        for (const route of routes) {
            const authorized = route.predicate
                ? route.predicate()
                : hasAbility(route.ability!)
            if (authorized) {
                navigate(route.path, { replace: true })
                return
            }
        }

        // Fallback — no permissions (matches home.js line 131)
        navigate('/no-permissions', { replace: true })
    }, [isProfileLoaded, hasAbility, userProfile, navigate, searchParams])

    return <RouteLoadingFallback />
}

// ========== Loading fallback ==========
function RouteLoadingFallback() {
    return (
        <div className="text-center" style={{ padding: '50px' }}>
            <LoadingIndicator />
        </div>
    )
}

// ========== Auth guard wrapper ==========
function RequireAuth({ children }: { children: React.ReactNode }) {
    const { isAuthenticated, isLoading } = useAuth()
    const location = useLocation()

    if (isLoading) {
        return (
            <div className="text-center" style={{ padding: '50px' }}>
                <LoadingIndicator />
            </div>
        )
    }

    if (!isAuthenticated) {
        // Pass current URL so LoginPage can preserve it through OIDC flow
        // (matches Angular security.showLogin({originalState}) pattern)
        return <Navigate to="/login" replace state={{ returnTo: location.pathname }} />
    }

    return <>{children}</>
}

// ========== Ability guard wrapper — matches Angular UI-Router resolve guards ==========
// When user lacks the required ability, Angular redirects to /home (which routes to
// first authorized page). This component replicates that behavior.
function RequireAbilityRoute({ ability, allOf, children }: {
    ability?: string
    allOf?: string[]
    children: React.ReactNode
}) {
    const { hasAbility, hasAllAbilities, isProfileLoaded } = useAbility()

    if (!isProfileLoaded) return <RouteLoadingFallback />

    let allowed = true
    if (ability) allowed = hasAbility(ability)
    if (allOf) allowed = hasAllAbilities(...allOf)

    if (!allowed) return <Navigate to="/home" replace />

    return <>{children}</>
}

export function AppRoutes() {
    return (
        <Suspense fallback={<RouteLoadingFallback />}>
            <Routes>
                {/* ============ Auth routes — match legacy AngularJS URLs ============ */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/loginCallback" element={<LoginCallbackPage />} />
                <Route path="/loginSilentCallback" element={<LoginSilentCallbackPage />} />
                <Route path="/logoutCallback" element={<LogoutCallbackPage />} />

                {/* ============ Protected routes ============ */}
                <Route path="/*" element={
                        <RequireAuth>
                            <Suspense fallback={<RouteLoadingFallback />}>
                                <Routes>
                                    {/* /home — ability-based routing (matches legacy home.js homeController) */}
                                    <Route path="home" element={<HomeRouter />} />
                                    {/* --- Priority 1: Analytics --- */}
                                    {/* Legacy: main.analytics = /analytics (abstract), main.analytics.report = /analytics/reports/:reportId — requires ClientViewReports */}
                                    <Route path="analytics" element={<AnalyticsModule />}>
                                        <Route path="reports/:reportId" element={<RequireAbilityRoute ability="ClientViewReports"><AnalyticsReportPage /></RequireAbilityRoute>} />
                                    </Route>

                                    {/* --- Priority 2: Reporting --- */}
                                    {/* Legacy: main.reporting = /reporting (abstract) */}
                                    <Route path="reporting" element={<ReportingLayout />}>
                                        <Route path="cip-analysis" element={<RequireAbilityRoute ability="GenerateCIPAnalysisReport"><CipAnalysisPage /></RequireAbilityRoute>} />
                                        <Route path="cip-defect-report" element={<RequireAbilityRoute ability="ViewCIPDefectReport"><CipDefectReportPage /></RequireAbilityRoute>} />
                                        <Route path="custom-risk-report" element={<RequireAbilityRoute ability="ViewCustomRiskReport"><CustomRiskReportPage /></RequireAbilityRoute>} />
                                        <Route path="arm-report" element={<RequireAbilityRoute ability="GenerateFraudReport"><ArmReportPage /></RequireAbilityRoute>} />
                                    </Route>

                                    {/* --- Priority 3: Dashboard (eager -- default landing) --- */}
                                    {/* Legacy: main.dashboard = /dashboard (abstract) */}
                                    <Route path="dashboard" element={<DashboardLayout />}>
                                        <Route index element={<Navigate to="disputes" replace />} />
                                        <Route path="disputes" element={<DisputesDashboardPage />} />
                                        <Route path="dispute-team-performance" element={<DisputeTeamPerformancePage />} />
                                    </Route>

                                    {/* --- Priority 7: Sanction List (ROOT-LEVEL routes) --- */}
                                    {/* Legacy: main.lookup = /lookup, main.results = /results, main.batchArchive = /batchArchive, main.clearedCustomers = /cleared-customers */}
                                    <Route path="lookup" element={<LookupPage />} />
                                    <Route path="results" element={<ResultsPage />} />
                                    <Route path="batchArchive" element={<BatchArchivePage />} />
                                    <Route path="batchArchive/:id" element={<BatchArchivePage />} />
                                    <Route path="cleared-customers" element={<ClearedCustomersPage />} />

                                    {/* --- Priority 8: Dispute Trends --- */}
                                    {/* Legacy: main.disputeTrends = /dispute-trends (abstract) */}
                                    <Route path="dispute-trends" element={<DisputeTrendsLayout />}>
                                        <Route path="transactions" element={<RequireAbilityRoute ability="ViewDisputeTrendTransactions"><DisputeTrendsTransactionsPage /></RequireAbilityRoute>} />
                                        {/* Legacy: main.disputeTrends.watchGroups = /watchgroups/:watchGroupId (note: no hyphen) */}
                                        <Route path="watchgroups/:watchGroupId" element={<WatchGroupLayout />}>
                                            <Route path="transactions" element={<WatchGroupTransactionsPage />} />
                                            <Route path="removed" element={<WatchGroupRemovedPage />} />
                                            <Route path="change-log" element={<WatchGroupChangeLogPage />} />
                                        </Route>
                                    </Route>

                                    {/* --- Priority 9: Watchlist Cases --- */}
                                    {/* Legacy: main.watchlistCases = /watchlistCases (camelCase!) */}
                                    <Route path="watchlistCases" element={<WatchlistCasesPage />} />
                                    <Route path="watchlistCases/:id" element={<WatchlistCaseLayout />}>
                                        {/* Legacy child states all have url: '' — they are tab-switched, not URL-navigated */}
                                        <Route index element={<CaseHistoryPage />} />
                                        <Route path="history" element={<CaseHistoryPage />} />
                                        <Route path="source-data" element={<CaseSourceDataPage />} />
                                        <Route path="notes" element={<CaseNotesPage />} />
                                        <Route path="match-review" element={<CaseMatchReviewPage />} />
                                        <Route path="files" element={<CaseFilesPage />} />
                                    </Route>

                                    {/* --- Priority 10: Document Management (main routes) --- */}
                                    {/* Legacy: main.docManagement = /docmgmt (abstract) */}
                                    <Route path="docmgmt" element={<DocManagementLayout />}>
                                        <Route path="queues" element={<QueuesPage />} />
                                        {/* Legacy: main.docManagement.queues.documents = /queues/:queueId/documents */}
                                        <Route path="queues/:queueId/documents" element={<QueueDocumentsPage />} />
                                        <Route path="uploadDocuments" element={<UploadDocumentsPage />} />
                                        <Route path="shared-documents" element={<SharedDocumentsPage />} />
                                        <Route path="shared-documents/:clientId" element={<SharedDocumentsForClientPage />} />
                                    </Route>

                                    {/* --- Priority 11: Reviews --- */}
                                    {/* Legacy: main.reviews = /reviews (abstract) */}
                                    <Route path="reviews" element={<ReviewsLayout />}>
                                        {/* Legacy: main.reviews.questionAndDenialReviews — data.ability: ViewReviews */}
                                        <Route path="status" element={<RequireAbilityRoute ability="ViewReviews"><QuestionAndDenialReviewsPage /></RequireAbilityRoute>} />
                                        {/* Legacy: main.reviews.chargeOffReviews — data.ability: ViewReviews */}
                                        <Route path="write-off" element={<RequireAbilityRoute ability="ViewReviews"><ChargeOffReviewsPage /></RequireAbilityRoute>} />
                                        {/* Legacy: main.reviews.lexisVerification = /verification (abstract)
                                            Angular uib-tabset renders both tab panels in the DOM simultaneously,
                                            so LexisVerificationLayout renders FlexIdPage + BestAddressPage inline
                                            (no Outlet). Child routes exist only for URL matching.
                                            Angular data.ability: ViewVerificationRequests — gate at parent level. */}
                                        <Route path="verification" element={<RequireAbilityRoute ability="ViewVerificationRequests"><LexisVerificationLayout /></RequireAbilityRoute>}>
                                            <Route path="bestAddress" element={null} />
                                            <Route path="flexId" element={null} />
                                        </Route>
                                    </Route>

                                    {/* --- Priority 12: Comp Cards (main routes — alerts/reissues) --- */}
                                    {/* Legacy: main.compCards = /comp-cards (abstract, alerts/reissues) */}
                                    <Route path="comp-cards" element={<CompCardsLayout />}>
                                        <Route path="alerts" element={<CompCardAlertsLayout />}>
                                            <Route index element={<CompCardAlertsListPage />} />
                                            <Route path="groups" element={<CompCardAlertGroupsPage />} />
                                        </Route>
                                        <Route path="reissues" element={<RequireAbilityRoute ability="ViewCompCardReissues"><CompCardReissuesPage /></RequireAbilityRoute>} />
                                        <Route path="reissues/:reissueId" element={<CompCardReissueLayout />}>
                                            <Route index element={<CompCardSummaryPage />} />
                                            <Route path="history" element={<CompCardHistoryPage />} />
                                            <Route path="related-cards" element={<CompCardRelatedCardsLayout />}>
                                                <Route index element={<CardsForReissuePage />} />
                                                <Route path="denied" element={<DeniedCardsPage />} />
                                            </Route>
                                            <Route path="recommendations" element={<RecommendationsPage />} />
                                            <Route path="related-programs" element={<RelatedProgramsPage />} />
                                            <Route path="call-blast" element={<CallBlastPage />} />
                                            <Route path="files" element={<CompCardFilesPage />} />
                                        </Route>
                                    </Route>

                                    {/* --- Priority 13: CSR Inquiries --- */}
                                    {/* Legacy: main.csrInquiries = /inquiries (abstract) */}
                                    <Route path="inquiries" element={<CsrInquiriesLayout />}>
                                        {/* Legacy: main.csrInquiries.csrFindCardholder = /new */}
                                        <Route path="new" element={<RequireAbilityRoute ability="CSRCreateCase"><CsrSearchPage /></RequireAbilityRoute>} />
                                        {/* Legacy: main.csrInquiries.csrNewDispute = /new-dispute (abstract) */}
                                        <Route path="new-dispute/:id" element={<CsrNewDisputeLayout />}>
                                            <Route path="transactions" element={<CsrTransactionsPage />} />
                                            <Route path="comments" element={<CsrCommentsPage />} />
                                            <Route path="questions" element={<CsrQuestionsPage />} />
                                            <Route path="disclosures" element={<CsrDisclosuresPage />} />
                                        </Route>
                                        <Route path="new-dispute/confirmation" element={<CsrConfirmationPage />} />
                                    </Route>

                                    {/* --- Priority 14: ARM (Advantage Risk Monitor) --- */}
                                    {/* Legacy: main.arms = /arm-alerts/:queueId */}
                                    <Route path="arm-alerts/:queueId" element={<ArmAlertQueuePage />} />
                                    <Route path="arm-alerts/:queueId/:id" element={<ArmCaseDetailLayout />}>
                                        <Route index element={<ArmCaseHistoryPage />} />
                                        <Route path="transactions" element={<ArmCaseTransactionsPage />} />
                                        <Route path="card-details" element={<ArmCaseCardDetailsPage />} />
                                        <Route path="customer-data" element={<ArmCaseCustomerDataPage />} />
                                        <Route path="files" element={<ArmCaseFilesPage />} />
                                        <Route path="notes" element={<ArmCaseNotesPage />} />
                                        <Route path="reports" element={<ArmCaseReportsPage />} />
                                    </Route>
                                    {/* Legacy: main.armCases = /arm-cases/:queueId */}
                                    <Route path="arm-cases/:queueId" element={<ArmCaseQueuePage />} />
                                    <Route path="arm-cases/:queueId/:id" element={<ArmCaseDetailLayout />}>
                                        <Route index element={<ArmCaseHistoryPage />} />
                                        <Route path="case-summary" element={<ArmCaseSummaryPage />} />
                                        <Route path="transactions" element={<ArmCaseTransactionsPage />} />
                                        <Route path="linked-alerts" element={<ArmCaseLinkedAlertsPage />} />
                                        <Route path="sar-forms" element={<ArmCaseSarFormsLayout />}>
                                            <Route path="list" element={<ArmSarFormsListPage />} />
                                            <Route path=":sarFormId" element={<ArmSarFormLayout />}>
                                                <Route path="home" element={<ArmSarFormHomePage />} />
                                                <Route path="contact-info" element={<ArmSarFormContactInfoPage />} />
                                                <Route path="activity-occurred" element={<ArmSarFormActivityOccurredPage />} />
                                                <Route path="subject-info" element={<ArmSarFormSubjectInfoPage />} />
                                                <Route path="suspicious-activity-info" element={<ArmSarFormSuspiciousActivityPage />} />
                                                <Route path="narrative" element={<ArmSarFormNarrativePage />} />
                                            </Route>
                                        </Route>
                                        <Route path="bsaClosure" element={<ArmBsaClosurePage />} />
                                        <Route path="card-details" element={<ArmCaseCardDetailsPage />} />
                                        <Route path="customer-data" element={<ArmCaseCustomerDataPage />} />
                                        <Route path="files" element={<ArmCaseFilesPage />} />
                                        <Route path="notes" element={<ArmCaseNotesPage />} />
                                        <Route path="reports" element={<ArmCaseReportsPage />} />
                                    </Route>
                                    {/* Legacy: main.irfReview = /irfReview */}
                                    <Route path="irfReview" element={<ArmIrfReviewPage />} />
                                    {/* Legacy: main.bsaReview = /bsaReview */}
                                    <Route path="bsaReview" element={<ArmBsaReviewPage />} />
                                    {/* Legacy: main.sarReviews = /sarReviews */}
                                    <Route path="sarReviews" element={<ArmSarReviewsPage />}>
                                        <Route path=":tab" element={<ArmSarReviewListPage />} />
                                    </Route>
                                    {/* Legacy: main.sarFormReviewContainer = /sarFormReview/:sarFormId */}
                                    <Route path="sarFormReview/:sarFormId" element={<ArmSarFormReviewContainerLayout />}>
                                        <Route path="home" element={<ArmSarFormHomePage />} />
                                        <Route path="contact-info" element={<ArmSarFormContactInfoPage />} />
                                        <Route path="activity-occurred" element={<ArmSarFormActivityOccurredPage />} />
                                        <Route path="subject-info" element={<ArmSarFormSubjectInfoPage />} />
                                        <Route path="suspicious-activity-info" element={<ArmSarFormSuspiciousActivityPage />} />
                                        <Route path="narrative" element={<ArmSarFormNarrativePage />} />
                                    </Route>
                                    {/* Legacy: main.eFilingSarBatch = /sarReview/eFilingSarBatch */}
                                    <Route path="sarReview/eFilingSarBatch" element={<SarFormBatchesPage />} />

                                    {/* Legacy: main.armSarFiled = /sar-filed */}
                                    <Route path="sar-filed" element={<ArmSarFiledPage />} />

                                    {/* --- Priority 15: Disputes --- */}
                                    {/* Legacy: main.disputes = /disputes (abstract) */}
                                    <Route path="disputes" element={<DisputesLayout />}>
                                        {/* Legacy: main.disputes.list.debit = /disputes + '' + /list/debit */}
                                        <Route path="list/debit" element={<RegEDisputesPage />} />
                                        {/* Legacy: main.disputes.list.credit = /disputes/list/credit — requires RegZ + ViewDisputes */}
                                        <Route path="list/credit" element={<RequireAbilityRoute allOf={['RegZ', 'ViewDisputes']}><RegZDisputesPage /></RequireAbilityRoute>} />
                                        {/* Legacy: main.disputes.list.debit.myCases = /disputes/list/debit/mycases */}
                                        <Route path="list/debit/mycases" element={<RegEDisputesPage />} />
                                        {/* Legacy: main.disputes.list.credit.myCases = /disputes/list/credit/mycases */}
                                        <Route path="list/credit/mycases" element={<RequireAbilityRoute allOf={['RegZ', 'ViewDisputes']}><RegZDisputesPage /></RequireAbilityRoute>} />
                                        {/* Legacy: main.disputes.casesprovisionalcredit = /disputes/casesprovisionalcredit — requires ViewProvisionalCreditCases */}
                                        <Route path="casesprovisionalcredit" element={<RequireAbilityRoute ability="ViewProvisionalCreditCases"><CasesProvisionalCreditPage /></RequireAbilityRoute>} />
                                        {/* Manual Case Entry wizard — nested under disputes */}
                                        {/* Legacy: main.disputes.list.manualCaseEntry = /disputes/list/debit/manualcase (and /credit/manualcase) */}
                                        <Route path="list/debit/manualcase" element={<ManualCaseEntryLayout />}>
                                            <Route index element={<ManualCaseEntryNewCaseTypePage />} />
                                            <Route path="case-type" element={<ManualCaseEntryNewCaseTypePage />} />
                                            <Route path="card" element={<ManualCaseEntryCardPage />} />
                                            <Route path="relatedtransactions" element={<ManualCaseEntryRelatedTransactionsPage />} />
                                            <Route path="caseinfo" element={<ManualCaseEntryCaseInfoPage />} />
                                        </Route>
                                        <Route path="list/credit/manualcase" element={<ManualCaseEntryLayout />}>
                                            <Route index element={<ManualCaseEntryNewCaseTypePage />} />
                                            <Route path="case-type" element={<ManualCaseEntryNewCaseTypePage />} />
                                            <Route path="card" element={<ManualCaseEntryCardPage />} />
                                            <Route path="relatedtransactions" element={<ManualCaseEntryRelatedTransactionsPage />} />
                                            <Route path="caseinfo" element={<ManualCaseEntryCaseInfoPage />} />
                                        </Route>
                                        {/* Legacy: main.disputes.list.debit.dispute = /disputes/list/debit/:id */}
                                        <Route path="list/debit/:id" element={<DisputeDetailLayout />}>
                                            <Route index element={<DisputeSummaryPage />} />
                                            <Route path="case-history" element={<DisputeHistoryPage />} />
                                            <Route path="disputed-transactions/:view" element={<DisputeTransactionsPage />} />
                                            <Route path="fees" element={<DisputeFeesPage />} />
                                            <Route path="card" element={<DisputeCardDetailsPage />} />
                                            <Route path="cardholder" element={<DisputeCardholderPage />} />
                                            <Route path="files" element={<DisputeFilesPage />} />
                                            <Route path="notes" element={<DisputeNotesPage />} />
                                            <Route path="communications" element={<DisputeCommunicationPage />} />
                                            <Route path="callcenternotes" element={<DisputeCallCenterNotesPage />} />
                                            <Route path="transaction-history" element={<DisputeTransactionHistoryPage />} />
                                        </Route>
                                        {/* Legacy: main.disputes.list.credit.dispute = /disputes/list/credit/:id */}
                                        <Route path="list/credit/:id" element={<DisputeDetailLayout />}>
                                            <Route index element={<DisputeSummaryPage />} />
                                            <Route path="case-history" element={<DisputeHistoryPage />} />
                                            <Route path="disputed-transactions/:view" element={<DisputeTransactionsPage />} />
                                            <Route path="fees" element={<DisputeFeesPage />} />
                                            <Route path="card" element={<DisputeCardDetailsPage />} />
                                            <Route path="cardholder" element={<DisputeCardholderPage />} />
                                            <Route path="files" element={<DisputeFilesPage />} />
                                            <Route path="notes" element={<DisputeNotesPage />} />
                                            <Route path="communications" element={<DisputeCommunicationPage />} />
                                            <Route path="callcenternotes" element={<DisputeCallCenterNotesPage />} />
                                            <Route path="transaction-history" element={<DisputeTransactionHistoryPage />} />
                                        </Route>
                                        {/* Legacy: main.disputes.processorApiLog = /disputes/processor-api-log — requires ViewProcessorApiLog */}
                                        <Route path="processor-api-log" element={<RequireAbilityRoute ability="ViewProcessorApiLog"><ProcessorApiLogPage /></RequireAbilityRoute>} />
                                        {/* Legacy: main.disputes.mastercomRequestLog = /disputes/mastercom-request-log — requires ViewMastercomRequestLog */}
                                        <Route path="mastercom-request-log" element={<RequireAbilityRoute ability="ViewMastercomRequestLog"><MastercomRequestLogPage /></RequireAbilityRoute>} />
                                        {/* Legacy: main.disputes.caseActivity = /disputes/caseActivityDashboard (abstract) */}
                                        <Route path="caseActivityDashboard/:tab" element={<RequireAbilityRoute ability="ViewActivityNotificationsDashboard"><CaseActivityDashboardPage /></RequireAbilityRoute>} />
                                        {/* Legacy: main.disputes.caseActivityInbox = /disputes/caseActivityInbox (abstract) */}
                                        <Route path="caseActivityInbox/:tab" element={<DisputeSearchPage />} />
                                        {/* Preserve generic search/results routes for React-native navigation */}
                                        <Route path="search" element={<DisputeSearchPage />} />
                                        <Route path="results" element={<DisputeResultsPage />} />
                                        <Route path=":disputeId" element={<DisputeDetailLayout />}>
                                            <Route index element={<DisputeSummaryPage />} />
                                            <Route path="history" element={<DisputeHistoryPage />} />
                                            <Route path="transactions" element={<DisputeTransactionsPage />} />
                                            <Route path="notes" element={<DisputeNotesPage />} />
                                            <Route path="files" element={<DisputeFilesPage />} />
                                            <Route path="letters" element={<DisputeLettersPage />} />
                                            <Route path="communication" element={<DisputeCommunicationPage />} />
                                        </Route>
                                    </Route>
                                    {/* Legacy: main.transactions = /transactions — requires ViewTransactions */}
                                    <Route path="transactions" element={<RequireAbilityRoute ability="ViewTransactions"><TransactionsPage /></RequireAbilityRoute>} />

                                    {/* --- Priority 16: Admin Settings --- */}
                                    {/* Legacy: main.admin = /settings (abstract) */}
                                    <Route path="settings" element={<AdminSettingsLayout />}>
                                        <Route index element={<AdminSettingsPage />} />

                                        {/* Legacy: main.admin.issuingBanks = /settings/issuing-banks */}
                                        <Route path="issuing-banks" element={<RequireAbilityRoute ability="ViewIssuingBanks"><AdminIssuingBanksPage /></RequireAbilityRoute>} />
                                        <Route path="issuing-banks/:issuingBankId" element={<RequireAbilityRoute ability="ViewIssuingBanks"><AdminIssuingBanksPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.processors = /settings/processors */}
                                        <Route path="processors" element={<RequireAbilityRoute ability="ViewProcessors"><AdminProcessorsPage /></RequireAbilityRoute>} />
                                        <Route path="processors/:processorId" element={<RequireAbilityRoute ability="ViewProcessors"><AdminProcessorsPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.report = /settings/reports/:reportId */}
                                        <Route path="reports/:reportId" element={<RequireAbilityRoute ability="ViewPowerBIReports"><AdminReportPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.bins = /settings/bins */}
                                        <Route path="bins" element={<RequireAbilityRoute ability="ManageBINs"><AdminBinsPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.fiids = /settings/fiids */}
                                        <Route path="fiids" element={<RequireAbilityRoute ability="ViewFIIDs"><AdminFiidsPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.cipPrograms = /settings/cip-programs */}
                                        <Route path="cip-programs" element={<RequireAbilityRoute ability="ManageProgramModels"><AdminCipProgramsPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.disputePrograms = /settings/dispute-programs */}
                                        <Route path="dispute-programs" element={<RequireAbilityRoute ability="ManageDisputePrograms"><AdminDisputeProgramsPage /></RequireAbilityRoute>} />
                                        <Route path="dispute-programs/program" element={<RequireAbilityRoute ability="ManageDisputePrograms"><AdminDisputeProgramsPage /></RequireAbilityRoute>} />
                                        <Route path="dispute-programs/searchresults" element={<RequireAbilityRoute ability="ManageDisputePrograms"><AdminDisputeProgramsPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.activatePrograms = /settings/activatePrograms */}
                                        <Route path="activatePrograms" element={<RequireAbilityRoute ability="ActivateProgramModels"><AdminActivateProgramsPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.issuingBankReportArchive = /settings/issuing-bank-report-archive */}
                                        <Route path="issuing-bank-report-archive" element={<RequireAbilityRoute ability="ViewIssuingBankReportArchive"><AdminIssuingBankReportArchivePage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.customers = /settings/customers */}
                                        <Route path="customers" element={<RequireAbilityRoute ability="ViewCustomers"><AdminCustomersPage /></RequireAbilityRoute>}>
                                            <Route path=":customerId" element={<CustomerDetailLayout />}>
                                                <Route path="details" element={<CustomerDetailsPage />} />
                                                <Route path="contact-billing" element={<CustomerContactBillingPage />} />
                                                <Route path="users" element={<CustomerUsersPage />} />
                                                <Route path="data-source" element={<CustomerDataSourcePage />} />
                                                <Route path="best-address" element={<CustomerBestAddressPage />} />
                                                <Route path="dispute-advantage" element={<CustomerDisputeAdvantagePage />} />
                                                <Route path="watchlists" element={<CustomerWatchlistsPage />} />
                                                <Route path="issuing-banks" element={<CustomerIssuingBanksPage />} />
                                                <Route path="standard-client" element={<CustomerStandardClientPage />} />
                                                <Route path="processors" element={<CustomerProcessorsPage />} />
                                                <Route path="external-teams" element={<CustomerExternalTeamsPage />} />
                                                <Route path="data-providers" element={<CustomerDataProviderPage />} />
                                                <Route path="csr-script" element={<CustomerCsrScriptPage />} />
                                            </Route>
                                        </Route>

                                        {/* Legacy: main.admin.integrationUsers = /settings/integration-users */}
                                        <Route path="integration-users" element={<RequireAbilityRoute ability="ViewIntegrationUsers"><AdminIntegrationUsersPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.disputeCaseDataApiSettings = /settings/dispute-case-data-api */}
                                        <Route path="dispute-case-data-api" element={<RequireAbilityRoute ability="ViewDisputeCaseDataApiSettings"><AdminDisputeCaseDataApiPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.clientIntegrationSettings = /settings/client-integration-settings */}
                                        <Route path="client-integration-settings" element={<RequireAbilityRoute ability="ViewIntegrationClientSettings"><AdminClientIntegrationSettingsPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.cipIntegrationSettings = /settings/cip-integration-settings */}
                                        <Route path="cip-integration-settings" element={<RequireAbilityRoute ability="ViewCIPIntegrationSettings"><AdminCipIntegrationSettingsPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.changeOfAddressIntegrationSettings = /settings/change-of-address-integration-settings */}
                                        <Route path="change-of-address-integration-settings" element={<RequireAbilityRoute ability="ViewChangeOfAddressIntegrationSettings"><AdminChangeOfAddressSettingsPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.tcIntegrationSettings = /settings/tc-export-integration-settings */}
                                        <Route path="tc-export-integration-settings" element={<RequireAbilityRoute ability="ViewVerificationRequestTCExportIntegrationSettings"><AdminTcIntegrationSettingsPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.addressverificationIntegrationSettings = /settings/address-verification-integration-settings */}
                                        <Route path="address-verification-integration-settings" element={<RequireAbilityRoute ability="ViewVerificationRequestFlexIdIntegrationSettings"><AdminAddressVerificationSettingsPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.cipProgramManagerReportIntegrationSettings = /settings/cip-program-manager-report-integration-settings */}
                                        <Route path="cip-program-manager-report-integration-settings" element={<RequireAbilityRoute ability="ViewCIPProgramManagerReportIntegrationSettings"><AdminCipPmReportSettingsPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.portalIntegrationSettings = /settings/portal-integration-settings */}
                                        <Route path="portal-integration-settings" element={<RequireAbilityRoute ability="ViewPortalIntegrationSettings"><AdminPortalIntegrationSettingsPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.exclusionList = /settings/exclusion-list */}
                                        <Route path="exclusion-list" element={<RequireAbilityRoute ability="ViewExclusionList"><AdminExclusionListPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.issuingBankExclusionList = /settings/issuing-bank-exclusion-list */}
                                        <Route path="issuing-bank-exclusion-list" element={<RequireAbilityRoute ability="ViewIssuingBankExclusionList"><AdminIssuingBankExclusionListPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.disputeIntegrationSettings = /settings/dispute-integration-settings */}
                                        <Route path="dispute-integration-settings" element={<RequireAbilityRoute ability="ViewDisputeIntegrationSettings"><AdminDisputeIntegrationSettingsPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.processorApiIntegrationSettings = /settings/processorapi-integration-settings */}
                                        <Route path="processorapi-integration-settings" element={<RequireAbilityRoute ability="ViewProcessorApiIntegrationSettings"><AdminProcessorApiIntegrationSettingsPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.modelTestIntegrationSettings = /settings/model-test-integration-settings */}
                                        <Route path="model-test-integration-settings" element={<RequireAbilityRoute ability="ViewModelTestIntegrationSettings"><AdminModelTestIntegrationSettingsPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.clientUsers = /settings/client-users */}
                                        <Route path="client-users" element={<RequireAbilityRoute ability="ViewClientUsers"><AdminClientUsersPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.manageUser = /settings/manage-user/:userId (abstract) */}
                                        <Route path="manage-user/:userId" element={<AdminManageUserLayout />} />

                                        {/* Legacy: main.admin.disputeProfiles = /settings/disputeProfiles */}
                                        <Route path="disputeProfiles" element={<RequireAbilityRoute ability="ViewDisputeProfiles"><AdminDisputeProfilesPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.manageDisputeProfile = /settings/manage-dispute-profile/:disputeProfileId (abstract) */}
                                        <Route path="manage-dispute-profile/:disputeProfileId" element={<AdminManageDisputeProfileLayout />} />

                                        {/* Legacy: main.admin.businessLines = /settings/business-lines */}
                                        <Route path="business-lines" element={<RequireAbilityRoute ability="ManageBusinessLines"><AdminBusinessLinesPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.disputeCaseSettings = /settings/dispute-case-settings */}
                                        <Route path="dispute-case-settings" element={<RequireAbilityRoute ability="EditDisputeCaseSettings"><AdminDisputeCaseSettingsPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.disputeCaseTimeframeSettings = /settings/dispute-case-timeframe-settings */}
                                        <Route path="dispute-case-timeframe-settings" element={<RequireAbilityRoute ability="EditCaseTimeframeSystemSettings"><AdminDisputeCaseTimeframeSettingsPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.disputeReason = /settings/dispute-reason */}
                                        <Route path="dispute-reason" element={<RequireAbilityRoute ability="EditDisputeReason"><AdminDisputeReasonPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.riskCodeManagement = /settings/risk-code-management */}
                                        <Route path="risk-code-management" element={<RequireAbilityRoute ability="ViewRiskCodeManagement"><AdminRiskCodeManagementPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.workflows = /settings/workflows */}
                                        <Route path="workflows" element={<RequireAbilityRoute ability="ManageWorkflows"><AdminWorkflowsPage /></RequireAbilityRoute>} />
                                        <Route path="workflows/:versionId" element={<AdminWorkflowsPage />} />
                                        <Route path="workflows/:versionId/edit" element={<WorkflowDesigner />} />

                                        {/* Legacy: main.admin.caseLetters = /settings/case-letters (abstract) */}
                                        <Route path="case-letters" element={<RequireAbilityRoute ability="ManageCaseLetters"><AdminCaseLettersLayout /></RequireAbilityRoute>}>
                                            <Route path="predefined-fields" element={<RequireAbilityRoute ability="ViewPreDefinedFields"><AdminPreDefinedFieldsPage /></RequireAbilityRoute>} />
                                            <Route path="print-export-fields" element={<RequireAbilityRoute ability="ManagePrintExportFields"><AdminPrintExportFieldsPage /></RequireAbilityRoute>} />
                                            <Route path="dispute-case-letters" element={<RequireAbilityRoute ability="ManageDisputeLetterTemplates"><AdminDisputeLetterPage /></RequireAbilityRoute>} />
                                            <Route path="dispute-letter-advanced-settings" element={<RequireAbilityRoute ability="ViewDisputeCustomerLetterDays"><AdminAdvancedSettingsPage /></RequireAbilityRoute>} />
                                        </Route>

                                        {/* Legacy: main.admin.programTypes = /settings/program-types */}
                                        <Route path="program-types" element={<RequireAbilityRoute ability="ViewProgramTypes"><AdminProgramTypesPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.disputeCloseReason = /settings/dispute-close-reason */}
                                        <Route path="dispute-close-reason" element={<RequireAbilityRoute ability="EditDisputeTransactionCloseReason"><AdminDisputeCloseReasonPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.transCodes = /settings/trans-codes */}
                                        <Route path="trans-codes" element={<RequireAbilityRoute ability="ManageTransCodes"><AdminTransCodesPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.disputeCallCenterAssignments = /settings/call-center-assignments (abstract) */}
                                        <Route path="call-center-assignments" element={<RequireAbilityRoute ability="EditCallCenterAssignments"><AdminCallCenterAssignmentsLayout /></RequireAbilityRoute>}>
                                            <Route path="my-call-centers" element={<AdminMyCallCentersPage />} />
                                            <Route path="assignments-by-bin" element={<AdminAssignmentsByBinPage />} />
                                        </Route>

                                        {/* Legacy: main.admin.redFlagReportIntegrationSettings = /settings/red-flag-report-integration-settings */}
                                        <Route path="red-flag-report-integration-settings" element={<RequireAbilityRoute ability="ViewRedFlagReportIntegrationSettings"><AdminRedFlagReportSettingsPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.armDailyReportIntegrationSettings = /settings/arm-daily-report-integration-settings */}
                                        <Route path="arm-daily-report-integration-settings" element={<RequireAbilityRoute ability="ViewFraudDailyReportIntegrationSettings"><AdminArmDailyReportSettingsPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.binAssignments = /settings/bin-assignments (abstract) */}
                                        <Route path="bin-assignments" element={<RequireAbilityRoute ability="EditBINAssignments"><AdminBinAssignmentsPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.autoAssign = /settings/auto-assign */}
                                        <Route path="auto-assign" element={<RequireAbilityRoute ability="ManageAutoAssign"><AdminAutoAssignPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.writeoffReason = /settings/writeoff-reason */}
                                        <Route path="writeoff-reason" element={<RequireAbilityRoute ability="EditWriteoffReason"><AdminWriteoffReasonPage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.disputeIntelligentIntake = /settings/intelligent-intake */}
                                        <Route path="intelligent-intake" element={<RequireAbilityRoute ability="EditDisputeCaseSettings"><AdminIntelligentIntakePage /></RequireAbilityRoute>} />
                                        {/* Legacy: main.admin.csrPermissions = /settings/csr-permissions (same page as intelligent-intake per Angular adminModule.js) */}
                                        <Route path="csr-permissions" element={<RequireAbilityRoute ability="EditDisputeCaseSettings"><AdminIntelligentIntakePage /></RequireAbilityRoute>} />

                                        {/* Legacy: main.admin.callNoteTemplates = /settings/note-templates */}
                                        <Route path="note-templates" element={<RequireAbilityRoute ability="EditDisputeNoteTemplate"><AdminNoteTemplatesPage /></RequireAbilityRoute>} />
                                        <Route path="note-templates/note-template" element={<AdminNoteTemplatesPage />} />

                                        {/* Legacy: main.admin.disputeCasePendTypes = /settings/dispute-pends */}
                                        <Route path="dispute-pends" element={<RequireAbilityRoute ability="EditDisputePends"><AdminDisputePendsPage /></RequireAbilityRoute>} />

                                        {/* ---- Debit Cases (main.admin.debit.*) ---- */}
                                        <Route path="debit/dispute-case-settings" element={<RequireAbilityRoute ability="EditDisputeCaseSettings"><AdminDisputeCaseSettingsPage /></RequireAbilityRoute>} />
                                        <Route path="debit/dispute-case-timeframe-settings" element={<RequireAbilityRoute ability="EditCaseTimeframeSystemSettings"><AdminDisputeCaseTimeframeSettingsPage /></RequireAbilityRoute>} />
                                        <Route path="debit/dispute-pends" element={<RequireAbilityRoute ability="EditDisputePends"><AdminDisputePendsPage /></RequireAbilityRoute>} />
                                        <Route path="debit/dispute-reason" element={<RequireAbilityRoute ability="EditDisputeReason"><AdminDisputeReasonPage /></RequireAbilityRoute>} />
                                        <Route path="debit/dispute-close-reason" element={<RequireAbilityRoute ability="EditDisputeTransactionCloseReason"><AdminDisputeCloseReasonPage /></RequireAbilityRoute>} />
                                        <Route path="debit/writeoff-reason" element={<RequireAbilityRoute ability="EditWriteoffReason"><AdminWriteoffReasonPage /></RequireAbilityRoute>} />
                                        <Route path="debit/auto-case-assignments" element={<RequireAbilityRoute ability="ManageAutoAssign"><AdminAutoAssignPage /></RequireAbilityRoute>} />
                                        <Route path="debit/note-templates" element={<RequireAbilityRoute ability="EditDisputeNoteTemplate"><AdminNoteTemplatesPage /></RequireAbilityRoute>} />
                                        <Route path="debit/note-templates/note-template" element={<RequireAbilityRoute ability="EditDisputeNoteTemplate"><AdminNoteTemplatesPage /></RequireAbilityRoute>} />
                                        <Route path="debit/case-letters" element={<RequireAbilityRoute ability="ManageCaseLetters"><AdminCaseLettersLayout /></RequireAbilityRoute>}>
                                            <Route path="predefined-fields" element={<RequireAbilityRoute ability="ViewPreDefinedFields"><AdminPreDefinedFieldsPage /></RequireAbilityRoute>} />
                                            <Route path="print-export-fields" element={<RequireAbilityRoute ability="ManagePrintExportFields"><AdminPrintExportFieldsPage /></RequireAbilityRoute>} />
                                            <Route path="dispute-case-letters" element={<RequireAbilityRoute ability="ManageDisputeLetterTemplates"><AdminDisputeLetterPage /></RequireAbilityRoute>} />
                                            <Route path="dispute-letter-advanced-settings" element={<RequireAbilityRoute ability="ViewDisputeCustomerLetterDays"><AdminAdvancedSettingsPage /></RequireAbilityRoute>} />
                                        </Route>

                                        {/* ---- Credit Cases (main.admin.credit.*) ---- */}
                                        <Route path="credit/dispute-pends" element={<RequireAbilityRoute allOf={["RegZ", "EditDisputePends"]}><AdminDisputePendsPage /></RequireAbilityRoute>} />
                                        <Route path="credit/dispute-reason" element={<RequireAbilityRoute allOf={["RegZ", "EditDisputeReason"]}><AdminDisputeReasonPage /></RequireAbilityRoute>} />
                                        <Route path="credit/dispute-close-reason" element={<RequireAbilityRoute allOf={["RegZ", "EditDisputeTransactionCloseReason"]}><AdminDisputeCloseReasonPage /></RequireAbilityRoute>} />
                                        <Route path="credit/writeoff-reason" element={<RequireAbilityRoute allOf={["RegZ", "EditWriteoffReason"]}><AdminWriteoffReasonPage /></RequireAbilityRoute>} />
                                        <Route path="credit/note-templates" element={<RequireAbilityRoute allOf={["RegZ", "EditDisputeNoteTemplate"]}><AdminNoteTemplatesPage /></RequireAbilityRoute>} />
                                        <Route path="credit/note-templates/note-template" element={<RequireAbilityRoute allOf={["RegZ", "EditDisputeNoteTemplate"]}><AdminNoteTemplatesPage /></RequireAbilityRoute>} />
                                        <Route path="credit/case-letters" element={<RequireAbilityRoute allOf={["RegZ", "ManageCaseLetters"]}><AdminCaseLettersLayout /></RequireAbilityRoute>}>
                                            <Route path="predefined-fields" element={<RequireAbilityRoute allOf={["RegZ", "ViewPreDefinedFields"]}><AdminPreDefinedFieldsPage /></RequireAbilityRoute>} />
                                            <Route path="print-export-fields" element={<RequireAbilityRoute allOf={["RegZ", "ManagePrintExportFields"]}><AdminPrintExportFieldsPage /></RequireAbilityRoute>} />
                                            <Route path="dispute-case-letters" element={<RequireAbilityRoute allOf={["RegZ", "ManageDisputeLetterTemplates"]}><AdminDisputeLetterPage /></RequireAbilityRoute>} />
                                            <Route path="dispute-letter-advanced-settings" element={<RequireAbilityRoute allOf={["RegZ", "ViewDisputeCustomerLetterDays"]}><AdminAdvancedSettingsPage /></RequireAbilityRoute>} />
                                        </Route>

                                        {/* Legacy: main.admin.operatingAccounts = /settings/operating-accounts */}
                                        <Route path="operating-accounts" element={<AdminOperatingAccountsPage />} />

                                        {/* Legacy: main.admin.holidayCalendar = /settings/holiday-calendar */}
                                        <Route path="holiday-calendar" element={<AdminHolidayCalendarPage />} />

                                        {/* Legacy: main.admin.sanctionedCountries = /settings/sanctioned-countries */}
                                        <Route path="sanctioned-countries" element={<SanctionedCountriesPage />} />

                                        {/* --- Branding (under settings) --- */}
                                        {/* Legacy: main.admin.branding = /settings/branding (abstract) */}
                                        <Route path="branding" element={<BrandingLayout />}>
                                            <Route path="config" element={<BrandingConfigPage />} />
                                        </Route>

                                        {/* --- Issuing Bank (under settings) --- */}
                                        {/* Legacy: main.admin.issuingBank = /settings/issuingBank (abstract) */}
                                        <Route path="issuingBank" element={<IssuingBankLayout />}>
                                            <Route path="settings" element={<IntegrationSettingsPage />} />
                                            <Route path="exclusion-settings" element={<ExclusionSettingsPage />} />
                                        </Route>

                                        {/* --- Portal (under settings) --- */}
                                        {/* Legacy: main.admin.portal = /settings/portal (abstract), children have /portal/category etc */}
                                        <Route path="portal" element={<PortalLayout />}>
                                            <Route path="portal/category" element={<PortalCategoryPage />} />
                                            <Route path="portal/apiCategory" element={<PortalAPICategoryPage />} />
                                            <Route path="portal/landingPage" element={<PortalLandingPage />} />
                                        </Route>

                                        {/* --- Comp Cards Criteria (under settings) --- */}
                                        {/* Legacy: main.admin.compCards.compCardCriteria = /settings/comp-cards/criteria */}
                                        <Route path="comp-cards" element={<CompCardsLayout />}>
                                            <Route path="criteria" element={<CompCardCriteriaPage />} />
                                        </Route>

                                        {/* --- Doc Management Admin (under settings) --- */}
                                        {/* Legacy: main.admin.docManagement = /settings/docmgmt (abstract) */}
                                        <Route path="docmgmt/manageQueues" element={<ManageQueuesPage />} />
                                        <Route path="docmgmt/archiveDocuments" element={<ArchiveDocumentsPage />} />

                                        {/* --- ARM Admin (under settings) --- */}
                                        {/* Legacy: main.admin.arm = /settings/arm (abstract) */}
                                        <Route path="arm/manage-queues" element={<RequireAbilityRoute ability="ManageFraudAlertQueues"><ArmManageQueuesPage /></RequireAbilityRoute>} />
                                        <Route path="arm/manage-suspicicious-activities" element={<ArmSuspiciousActivityTypesPage />} />
                                    </Route>

                                    {/* Legacy: main.authenticationSettings = /authentication-settings (NOT under /settings/) */}
                                    <Route path="authentication-settings" element={<AdminAuthenticationSettingsPage />} />

                                    {/* --- Fallback --- */}
                                    <Route path="no-permissions" element={<NoPermissionsPage />} />
                                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                                </Routes>
                            </Suspense>
                        </RequireAuth>
                    }
                />
            </Routes>
        </Suspense>
    )
}
